import type { Caja } from "./recorte"

/**
 * La tarjeta de cumpleaños, dibujada en un lienzo (24/10). Es el MISMO lienzo que se ve y
 * que se descarga: una vista aparte sería una segunda verdad, como la rotulación.
 *
 * ⚠ **Lienzo y no SVG**: un SVG exportado a imagen pierde las fuentes que no lleve
 * incrustadas; en un lienzo, la que la página ya cargó (Cinzel y Montserrat, en
 * `index.html`) se dibuja tal cual. Por eso `fuentesListas()` espera a que lleguen.
 *
 * ⚠ **Los colores son los del papel impreso** (`--impreso-*`, la firma corporativa): el
 * vino y el dorado de la marca. Se leen del CSS, no se escriben aquí.
 *
 * El diseño es el de la tarjeta de referencia: papel crema, globos vino y dorado a los
 * lados, confeti, «¡FELIZ CUMPLEAÑOS!», la foto en un anillo dorado, el nombre, el
 * mensaje, el sello de los 25 años con la agencia, el QR a la web y la fecha.
 */

export const ANCHO = 1080
export const ALTO = 1400
const FOTO = { x: 540, y: 575, r: 228 }

export type Encuadre = {
  /** 1 = el primer plano automático; más es más cerca. */
  zoom: number
  /** Desplazamiento, en fracciones del lado del recorte. */
  dx: number
  dy: number
}

export type DatosTarjeta = {
  foto: HTMLCanvasElement | null
  cara: Caja | null
  encuadre: Encuadre
  nombre: string
  mensaje: string
  fecha: string
  agencia: string
  logo: HTMLImageElement | null
  qr: HTMLCanvasElement | null
}

type Paleta = Record<
  | "papel"
  | "tinta"
  | "gris"
  | "vino"
  | "vinoClaro"
  | "vinoOscuro"
  | "oro1"
  | "oro2"
  | "oro3"
  | "oro4"
  | "crema1"
  | "crema2",
  string
>

/** Lee la paleta impresa del CSS, desde una pieza invisible que la declara. */
export function leerPaleta(): Paleta {
  const pieza = document.createElement("div")
  pieza.className = "pieza-impresa"
  pieza.style.display = "none"
  document.body.appendChild(pieza)
  const css = getComputedStyle(pieza)
  const v = (nombre: string) => css.getPropertyValue(nombre).trim()
  const paleta: Paleta = {
    papel: v("--impreso-papel"),
    tinta: v("--impreso-tinta"),
    gris: v("--impreso-gris"),
    vino: v("--impreso-vino"),
    vinoClaro: v("--impreso-panel-2"),
    vinoOscuro: v("--impreso-banda-1"),
    oro1: v("--impreso-oro-1"),
    oro2: v("--impreso-oro-2"),
    oro3: v("--impreso-oro-3"),
    oro4: v("--impreso-oro-4"),
    crema1: v("--impreso-crema-1"),
    crema2: v("--impreso-crema-2"),
  }
  pieza.remove()
  return paleta
}

/** Espera a las fuentes de la tarjeta; si no llegan (sin internet), dibuja con las de respaldo. */
export async function fuentesListas(): Promise<void> {
  try {
    await Promise.all([
      document.fonts.load("700 96px Cinzel"),
      document.fonts.load("800 70px Montserrat"),
      document.fonts.load("500 36px Montserrat"),
      document.fonts.load("600 36px Montserrat"),
    ])
  } catch {
    // Degrada, no truena: igual que la firma de correo.
  }
}

/** Un generador pseudoaleatorio con semilla: el confeti cae siempre igual. */
function azar(semilla: number): () => number {
  let s = semilla >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 4294967296
  }
}

function globo(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  claro: string,
  base: string,
  oscuro: string,
) {
  // El hilo, ondulado hacia abajo.
  ctx.save()
  ctx.strokeStyle = oscuro
  ctx.globalAlpha = 0.55
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(x, y + r)
  ctx.bezierCurveTo(x - 22, y + r + 60, x + 22, y + r + 110, x - 6, y + r + 190)
  ctx.stroke()
  ctx.restore()
  // El cuerpo, con el brillo arriba a la izquierda.
  const g = ctx.createRadialGradient(x - r * 0.35, y - r * 0.4, r * 0.1, x, y, r * 1.1)
  g.addColorStop(0, claro)
  g.addColorStop(0.45, base)
  g.addColorStop(1, oscuro)
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.ellipse(x, y, r * 0.84, r, 0, 0, Math.PI * 2)
  ctx.fill()
  // El nudo.
  ctx.fillStyle = oscuro
  ctx.beginPath()
  ctx.moveTo(x - 9, y + r + 12)
  ctx.lineTo(x + 9, y + r + 12)
  ctx.lineTo(x, y + r - 3)
  ctx.closePath()
  ctx.fill()
  // El reflejo.
  ctx.save()
  ctx.globalAlpha = 0.35
  ctx.fillStyle = "white"
  ctx.beginPath()
  ctx.ellipse(x - r * 0.38, y - r * 0.42, r * 0.12, r * 0.24, -0.5, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

function confeti(ctx: CanvasRenderingContext2D, p: Paleta) {
  const r = azar(2025)
  const libre = (x: number, y: number) => {
    const enFoto = Math.hypot(x - FOTO.x, y - FOTO.y) < FOTO.r + 60
    const enTexto = x > 120 && x < 960 && y > 820 && y < 1120
    const enTitulo = x > 150 && x < 930 && y > 70 && y < 300
    return !enFoto && !enTexto && !enTitulo
  }
  let puestos = 0
  for (let i = 0; i < 400 && puestos < 46; i++) {
    const x = 60 + r() * (ANCHO - 120)
    const y = 40 + r() * 1080
    if (!libre(x, y)) continue
    puestos++
    ctx.save()
    ctx.translate(x, y)
    ctx.rotate(r() * Math.PI)
    ctx.globalAlpha = 0.55 + r() * 0.35
    if (r() < 0.6) {
      ctx.fillStyle = r() < 0.5 ? p.oro2 : p.oro1
      ctx.fillRect(-9, -3.5, 18, 7)
    } else {
      ctx.strokeStyle = p.oro2
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.moveTo(-14, 0)
      ctx.bezierCurveTo(-6, -12, 6, 12, 14, 0)
      ctx.stroke()
    }
    ctx.restore()
  }
}

function esquinas(ctx: CanvasRenderingContext2D, p: Paleta) {
  for (const lado of [1, -1]) {
    ctx.save()
    if (lado === -1) {
      ctx.translate(ANCHO, 0)
      ctx.scale(-1, 1)
    }
    const g = ctx.createLinearGradient(0, ALTO - 260, 260, ALTO)
    g.addColorStop(0, p.vinoClaro)
    g.addColorStop(1, p.vinoOscuro)
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.moveTo(0, ALTO - 250)
    ctx.bezierCurveTo(70, ALTO - 170, 160, ALTO - 60, 300, ALTO)
    ctx.lineTo(0, ALTO)
    ctx.closePath()
    ctx.fill()
    // El filete dorado que acompaña la curva, como en la firma corporativa.
    ctx.strokeStyle = p.oro2
    ctx.lineWidth = 4
    ctx.beginPath()
    ctx.moveTo(0, ALTO - 272)
    ctx.bezierCurveTo(80, ALTO - 186, 172, ALTO - 72, 322, ALTO)
    ctx.stroke()
    ctx.restore()
  }
}

/** Escribe centrado, encogiendo la letra hasta que quepa en `maximo`. */
function centrado(
  ctx: CanvasRenderingContext2D,
  texto: string,
  y: number,
  peso: string,
  cuerpo: number,
  familia: string,
  maximo: number,
) {
  let tam = cuerpo
  ctx.font = `${peso} ${tam}px ${familia}`
  while (ctx.measureText(texto).width > maximo && tam > 20) {
    tam -= 2
    ctx.font = `${peso} ${tam}px ${familia}`
  }
  ctx.fillText(texto, ANCHO / 2, y)
}

/** Parte el mensaje en líneas que quepan, por palabras (nunca por letras). */
function lineas(ctx: CanvasRenderingContext2D, texto: string, maximo: number): string[] {
  const salida: string[] = []
  for (const parrafo of texto.split("\n")) {
    let actual = ""
    for (const palabra of parrafo.split(/\s+/).filter(Boolean)) {
      const prueba = actual ? `${actual} ${palabra}` : palabra
      if (ctx.measureText(prueba).width > maximo && actual) {
        salida.push(actual)
        actual = palabra
      } else {
        actual = prueba
      }
    }
    if (actual) salida.push(actual)
  }
  return salida
}

/** El recorte de la foto: primer plano desde la cara, con el ajuste de quien edita. */
export function recorteDe(
  ancho: number,
  alto: number,
  cara: Caja | null,
  e: Encuadre,
): { x: number; y: number; lado: number } {
  const base = cara ? cara.alto * 2.2 : Math.min(ancho, alto) * 0.92
  const lado = base / e.zoom
  const cx = (cara ? cara.x + cara.ancho / 2 : ancho / 2) + e.dx * lado
  // La cara un poco por encima del centro: entran el cuello y los hombros.
  const cy = (cara ? cara.y + cara.alto * 0.72 : alto / 2) + e.dy * lado
  return { x: cx - lado / 2, y: cy - lado / 2, lado }
}

export function dibujarTarjeta(lienzo: HTMLCanvasElement, d: DatosTarjeta, p: Paleta): void {
  const ctx = lienzo.getContext("2d")
  if (!ctx) return
  lienzo.width = ANCHO
  lienzo.height = ALTO
  ctx.textAlign = "center"
  ctx.textBaseline = "alphabetic"

  // El papel.
  const papel = ctx.createRadialGradient(540, 620, 120, 540, 700, 980)
  papel.addColorStop(0, p.crema1)
  papel.addColorStop(1, p.crema2)
  ctx.fillStyle = papel
  ctx.fillRect(0, 0, ANCHO, ALTO)

  confeti(ctx, p)

  // Los globos, a los lados, como en la referencia.
  const vino: [string, string, string] = [p.vinoClaro, p.vino, p.vinoOscuro]
  const oro: [string, string, string] = [p.oro3, p.oro2, p.oro4]
  const globos: [number, number, number, [string, string, string]][] = [
    [40, 150, 92, oro],
    [150, 70, 78, vino],
    [52, 420, 84, vino],
    [-6, 640, 80, oro],
    [1040, 150, 92, vino],
    [930, 66, 78, oro],
    [1030, 420, 84, oro],
    [1092, 640, 80, vino],
    [945, 440, 64, vino],
    [135, 440, 64, oro],
  ]
  for (const [x, y, r, [c, b, o]] of globos) globo(ctx, x, y, r, c, b, o)

  // El marco: un filete dorado fino a los lados.
  ctx.strokeStyle = p.oro2
  ctx.lineWidth = 3
  ctx.strokeRect(22, 22, ANCHO - 44, ALTO - 44)
  esquinas(ctx, p)

  // El título.
  ctx.fillStyle = p.vinoOscuro
  centrado(ctx, "¡FELIZ", 175, "700", 104, "Cinzel, Georgia, serif", 760)
  centrado(ctx, "CUMPLEAÑOS!", 290, "700", 104, "Cinzel, Georgia, serif", 820)

  // La foto, en su anillo dorado.
  ctx.save()
  ctx.shadowColor = "rgba(0,0,0,0.18)"
  ctx.shadowBlur = 30
  ctx.shadowOffsetY = 10
  const anillo = ctx.createLinearGradient(FOTO.x - 260, FOTO.y - 260, FOTO.x + 260, FOTO.y + 260)
  anillo.addColorStop(0, p.oro1)
  anillo.addColorStop(0.35, p.oro2)
  anillo.addColorStop(0.6, p.oro3)
  anillo.addColorStop(1, p.oro4)
  ctx.fillStyle = anillo
  ctx.beginPath()
  ctx.arc(FOTO.x, FOTO.y, FOTO.r + 22, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
  const fondoFoto = ctx.createRadialGradient(FOTO.x, FOTO.y - 60, 20, FOTO.x, FOTO.y, FOTO.r)
  fondoFoto.addColorStop(0, p.papel)
  fondoFoto.addColorStop(1, p.crema1)
  ctx.fillStyle = fondoFoto
  ctx.beginPath()
  ctx.arc(FOTO.x, FOTO.y, FOTO.r, 0, Math.PI * 2)
  ctx.fill()
  if (d.foto) {
    const c = recorteDe(d.foto.width, d.foto.height, d.cara, d.encuadre)
    ctx.save()
    ctx.beginPath()
    ctx.arc(FOTO.x, FOTO.y, FOTO.r, 0, Math.PI * 2)
    ctx.clip()
    ctx.drawImage(
      d.foto,
      c.x,
      c.y,
      c.lado,
      c.lado,
      FOTO.x - FOTO.r,
      FOTO.y - FOTO.r,
      FOTO.r * 2,
      FOTO.r * 2,
    )
    ctx.restore()
  }
  ctx.strokeStyle = p.oro4
  ctx.lineWidth = 3
  ctx.beginPath()
  ctx.arc(FOTO.x, FOTO.y, FOTO.r + 1, 0, Math.PI * 2)
  ctx.stroke()

  // El nombre y el mensaje.
  ctx.fillStyle = p.vinoOscuro
  centrado(ctx, d.nombre.toUpperCase(), 935, "800", 76, "Montserrat, Arial, sans-serif", 900)
  ctx.fillStyle = p.tinta
  ctx.font = "500 38px Montserrat, Arial, sans-serif"
  const renglones = lineas(ctx, d.mensaje, 820).slice(0, 4)
  renglones.forEach((l, i) => {
    ctx.fillText(l, ANCHO / 2, 1010 + i * 54)
  })

  // El sello de los 25 años, la agencia y el QR.
  const pieY = 1165
  let bloqueX = 250
  if (d.logo) {
    const alto = 96
    const ancho = (d.logo.width / d.logo.height) * alto
    ctx.drawImage(d.logo, bloqueX, pieY, ancho, alto)
    bloqueX += ancho + 14
  }
  ctx.textAlign = "left"
  ctx.fillStyle = p.vinoOscuro
  ctx.font = "700 40px Montserrat, Arial, sans-serif"
  ctx.fillText("Años", bloqueX, pieY + 42)
  ctx.fillStyle = p.gris
  ctx.font = "500 24px Montserrat, Arial, sans-serif"
  ctx.fillText("Evolucionando la Logística", bloqueX, pieY + 78)
  ctx.textAlign = "center"
  if (d.agencia) {
    ctx.fillStyle = p.vinoOscuro
    // Centrada respecto a la TARJETA (pedido del usuario), y sin llegar al QR: encoge si
    // el nombre no cabe en 520 px.
    centrado(
      ctx,
      d.agencia.toUpperCase(),
      pieY + 132,
      "700",
      27,
      "Montserrat, Arial, sans-serif",
      520,
    )
  }
  if (d.qr) {
    const lado = 150
    const qx = 840
    const qy = pieY - 8
    ctx.fillStyle = p.papel
    ctx.strokeStyle = p.oro2
    ctx.lineWidth = 3
    ctx.beginPath()
    ctx.roundRect(qx - 12, qy - 12, lado + 24, lado + 24, 14)
    ctx.fill()
    ctx.stroke()
    ctx.drawImage(d.qr, qx, qy, lado, lado)
  }
  ctx.fillStyle = p.vinoOscuro
  centrado(ctx, d.fecha.toUpperCase(), 1356, "700", 40, "Montserrat, Arial, sans-serif", 560)
}
